import { useState } from 'react';
import Button from '../../../shared/components/Button';
import { TextArea } from '../../../shared/components/TextArea';
import { useAddSalesContactActivity, useSalesContactActivities } from '../hooks/useSalesContacts';
import { ACTIVITY_TYPE_LABELS, SalesContactActivityType } from '../types';

const formatDate = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

const TYPE_CLASSES: Record<SalesContactActivityType, string> = {
  NOTE: 'bg-muted text-muted-foreground',
  STAGE_CHANGE: 'bg-blue-100 text-blue-700',
  INTAKE_UPDATE: 'bg-amber-100 text-amber-800',
};

/**
 * Activity timeline for one contact, plus the note composer.
 *
 * The list shape is lifted from FeatureFlagsPage's AuditPanel — a `<ul>` of timestamped
 * entries — since both are the same thing: an append-only history nobody edits.
 *
 * Only NOTE is offered in the composer. STAGE_CHANGE is written by order-service itself when
 * `stage` changes, and INTAKE_UPDATE by the public lead-intake route; letting a human forge
 * either would make the timeline lie about where a change came from.
 */
export function ContactActivityPanel({ contactId }: { contactId: string }) {
  const { data: activities = [], isLoading, error } = useSalesContactActivities(contactId);
  const addActivity = useAddSalesContactActivity();
  const [body, setBody] = useState('');

  const submit = async () => {
    const trimmed = body.trim();
    if (!trimmed) return;
    await addActivity.mutateAsync({
      id: contactId,
      payload: { body: trimmed, activityType: SalesContactActivityType.NOTE },
    });
    setBody('');
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6 space-y-4">
      <div>
        <h2 className="text-base font-semibold text-foreground">Activity</h2>
        <p className="text-sm text-muted-foreground mt-0.5">
          Notes you add, plus every stage change recorded automatically.
        </p>
      </div>

      <div className="space-y-2">
        <TextArea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Add a note — what was said, what happens next…"
        />
        <div className="flex justify-end">
          <Button
            size="sm"
            className="h-[40px]"
            onClick={submit}
            disabled={!body.trim() || addActivity.isPending}
            loading={addActivity.isPending}
          >
            Add note
          </Button>
        </div>
      </div>

      {isLoading && <p className="text-sm text-muted-foreground">Loading activity…</p>}
      {error && <p className="text-sm text-red-500">Could not load this contact's activity.</p>}
      {!isLoading && !error && activities.length === 0 && (
        <p className="text-sm text-muted-foreground">No activity yet.</p>
      )}

      {activities.length > 0 && (
        <ul className="space-y-3 border-t border-border pt-4">
          {activities.map((activity) => (
            <li key={activity.id} className="text-sm text-foreground">
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={`text-xs px-2 py-0.5 rounded-full ${TYPE_CLASSES[activity.activityType]}`}
                >
                  {ACTIVITY_TYPE_LABELS[activity.activityType]}
                </span>
                <span className="text-muted-foreground text-xs">
                  {formatDate(activity.createdAt)}
                  {/* `createdById` is nullable (system-written entries have no author) — say
                      "System" rather than rendering a dangling "by ". */}
                  {activity.createdById ? ` · by ${activity.createdById}` : ' · System'}
                </span>
              </div>
              <p className="mt-1 whitespace-pre-wrap">{activity.body}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
